<template>
  <div class="page-wrapper">

    <!-- VERIFICATION MODAL -->
    <div v-if="showVerification" class="modal fade show d-block modal-overlay">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content text-center p-4">
          <div class="verification-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"></path>
            </svg>
          </div>
          <h3 class="mt-3">Verify Your Account</h3>
          <p class="text-muted">We've sent a verification code to</p>
          <p class="phone-number">{{ maskedPhone }}</p>
          
          <div class="code-inputs mt-3">
            <input 
              v-for="(digit, index) in 6" 
              :key="index"
              type="text"
              maxlength="1"
              class="code-input"
              v-model="verificationCode[index]"
              @input="handleCodeInput(index, $event)"
              @keyup="handleCodeKeyup(index, $event)"
              ref="codeInputs"
            />
          </div>

          <div class="mt-3">
            <button 
              class="btn btn-primary w-100" 
              @click="verifyAccount"
              :disabled="verifying || !isCodeComplete"
            >
              <span v-if="verifying" class="spinner-border spinner-border-sm me-2"></span>
              {{ verifying ? "Verifying..." : "Verify Account" }}
            </button>
          </div>

          <div class="mt-3">
            <button 
              class="btn btn-link" 
              @click="resendCode"
              :disabled="resendTimer > 0"
            >
              {{ resendTimer > 0 ? `Resend code in ${resendTimer}s` : "Resend Code" }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- SUCCESS MODAL -->
    <div v-if="showSuccess" class="modal fade show d-block modal-overlay">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content text-center p-5">
          <i class="bi bi-check-circle-fill success-icon"></i>
          <h3>Registration Successful!</h3>
          <p class="text-muted">Your account has been created and verified.</p>
          <button @click="goToLogin" class="btn btn-success w-100">
            Go to Login
          </button>
        </div>
      </div>
    </div>

    <!-- MAIN CARD -->
    <div class="form-container">
      <div class="card shadow-lg border-0">

        <div class="card-body">

          <h2 class="text-center mb-4">Create Account</h2>

          <form @submit.prevent="handleSubmit">

            <div class="row g-4">

              <!-- LEFT COLUMN -->
              <div class="col-md-6">

                <h5 class="section-title">
                  <i class="bi bi-building me-2"></i>Business Info
                </h5>

                <input v-model="form.Business_name" class="form-control mb-2" placeholder="Business Name" required />

                <input v-model="form.Business_phone" class="form-control mb-2" placeholder="Business Phone" required />

                <input v-model="form.Business_address.poBox" class="form-control mb-2" placeholder="P.O Box" required />

                <input v-model="form.Business_address.location" class="form-control mb-2" placeholder="Location" required />

              </div>

              <!-- RIGHT COLUMN -->
              <div class="col-md-6">

                <h5 class="section-title">
                  <i class="bi bi-person-circle me-2"></i>Account Info
                </h5>

                <input v-model="form.email" type="email" class="form-control mb-2" placeholder="Email" required />

                <input v-model="form.phone_number" class="form-control mb-2" placeholder="Phone Number" required />

                <div class="input-group mb-2">
                  <input v-model="form.password" :type="showPass ? 'text':'password'" class="form-control" placeholder="Password" required />
                  <button type="button" class="btn btn-outline-secondary" @click="showPass=!showPass">
                    <i :class="showPass ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>

                <div class="input-group mb-2">
                  <input v-model="form.confirmPassword" :type="showConfirm ? 'text':'password'" class="form-control" placeholder="Confirm Password" required />
                  <button type="button" class="btn btn-outline-secondary" @click="showConfirm=!showConfirm">
                    <i :class="showConfirm ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>

                <small v-if="form.password !== form.confirmPassword && form.confirmPassword" class="text-danger">
                  Passwords do not match
                </small>

              </div>

            </div>

            <!-- ERROR -->
            <div v-if="error" class="alert alert-danger mt-3">
              {{ error }}
            </div>

            <!-- SUBMIT -->
            <button class="btn btn-primary w-100 mt-3" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ loading ? 'Creating...' : 'Create Account' }}
            </button>

            <div class="text-center mt-2">
              <small>
                Already have an account?
                <a href="#" @click.prevent="goToLogin">Login</a>
              </small>
            </div>

          </form>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'RegistrationForm',
  data() {
    return {
      form: {
        Business_name: '',
        Business_phone: '',
        Business_address: {
          poBox: '',
          location: ''
        },
        email: '',
        phone_number: '',
        password: '',
        confirmPassword: ''
      },
      error: '',
      loading: false,
      showPass: false,
      showConfirm: false,
      showSuccess: false,
      showVerification: false,
      verifying: false,
      maskedPhone: '',
      verificationCode: ['', '', '', '', '', ''],
      resendTimer: 0,
      API_BASE_URL: 'https://golang.roberms.com/api'//'https://golang.roberms.com/api'
    }
  },
  computed: {
    isCodeComplete() {
      return this.verificationCode.every(digit => digit && digit.length === 1);
    },
    fullVerificationCode() {
      return this.verificationCode.join("");
    }
  },
  methods: {
    handleCodeInput(index, event) {
      const value = event.target.value;
      if (value && /^\d$/.test(value)) {
        this.verificationCode[index] = value;
        if (index < 5) {
          this.$refs.codeInputs[index + 1].focus();
        }
      } else if (value === "") {
        this.verificationCode[index] = "";
      } else {
        this.verificationCode[index] = "";
      }
    },

    handleCodeKeyup(index, event) {
      if (event.key === 'Backspace' && index > 0 && !this.verificationCode[index]) {
        this.$refs.codeInputs[index - 1].focus();
      }
    },

    async handleSubmit() {
      if (this.form.password !== this.form.confirmPassword) {
        this.error = 'Passwords do not match'
        return
      }

      // Validate business address fields
      if (!this.form.Business_address.poBox || !this.form.Business_address.location) {
        this.error = 'Please provide both P.O. Box and Location for Business Address'
        return
      }

      this.loading = true
      this.error = ''

      // Prepare the data for API submission
      const submitData = {
        Business_name: this.form.Business_name,
        Business_phone: this.form.Business_phone,
        Business_address: `${this.form.Business_address.poBox}, ${this.form.Business_address.location}`,
        email: this.form.email,
        phone_number: this.form.phone_number,
        password: this.form.password
      }

      try {
        const response = await fetch(`${this.API_BASE_URL}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submitData)
        })

        const data = await response.json()
        
        if (!response.ok) throw new Error(data.error || 'Registration failed')
        
        // Show verification modal instead of success
        this.maskedPhone = data.data.phone_number
        this.showVerification = true
        this.startResendTimer()
        
        // Reset form
        this.form = {
          Business_name: '',
          Business_phone: '',
          Business_address: {
            poBox: '',
            location: ''
          },
          email: '',
          phone_number: '',
          password: '',
          confirmPassword: ''
        }
        
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },

    async verifyAccount() {
      if (!this.isCodeComplete) return;

      this.verifying = true;
      this.error = '';

      try {
        const response = await fetch(`${this.API_BASE_URL}/verify/account`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            verification_code: this.fullVerificationCode
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Verification failed');
        }

        // Close verification modal and show success
        this.showVerification = false;
        this.showSuccess = true;
        
        // Store token for auto-login if needed
        if (data.data && data.data.token) {
          localStorage.setItem('token', data.data.token);
          localStorage.setItem('user', JSON.stringify(data.data.user));
        }
        
      } catch (err) {
        this.error = err.message;
        alert(this.error); // Show error to user
      } finally {
        this.verifying = false;
      }
    },

    async resendCode() {
      if (this.resendTimer > 0) return;

      try {
        const response = await fetch(`${this.API_BASE_URL}/resend-verification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phone_number: this.form.phone_number
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to resend code');
        }

        // Reset code inputs
        this.verificationCode = ['', '', '', '', '', ''];
        if (this.$refs.codeInputs && this.$refs.codeInputs[0]) {
          this.$refs.codeInputs[0].focus();
        }
        
        this.startResendTimer();
        alert('New verification code sent!');
        
      } catch (err) {
        alert(err.message);
      }
    },

    startResendTimer() {
      this.resendTimer = 60;
      const interval = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer--;
        } else {
          clearInterval(interval);
        }
      }, 1000);
    },

    goToLogin() {
      this.$router.push('/login')
    }
  }
}
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  background: var(--dash-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.form-container {
  width: 100%;
  max-width: 950px;
}

.card {
  border-radius: var(--dash-radius-lg);
}

.card-body {
  padding: 30px;
}

.section-title {
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--dash-primary-hover);
}

.form-control {
  height: 42px;
  border-radius: var(--dash-radius);
}

.btn-primary {
  background: var(--dash-primary);
  border: none;
}

.btn-primary:hover {
  transform: translateY(-2px);
}

.modal-overlay {
  background: rgba(0,0,0,0.6);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  margin: auto;
}

.modal-content {
  border-radius: var(--dash-radius-lg);
}

.success-icon {
  font-size: 2.25rem;
  color: var(--dash-success);
  margin-bottom: 15px;
}

.verification-icon {
  width: 80px;
  height: 80px;
  background: var(--dash-primary-soft);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  color: var(--dash-primary-ink);
}

.verification-icon svg {
  color: var(--dash-primary-ink);
}

.phone-number {
  font-size: 18px;
  font-weight: 600;
  color: var(--dash-primary);
  margin: 10px 0 5px;
}

.code-inputs {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.code-input {
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  transition: all 0.3s ease;
}

.code-input:focus {
  outline: none;
  border-color: var(--dash-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.btn-link {
  text-decoration: none;
  color: var(--dash-primary);
}

.btn-link:hover {
  text-decoration: underline;
}

.btn-link:disabled {
  color: var(--dash-faint);
  cursor: not-allowed;
}

/* MOBILE */
@media (max-width: 768px) {
  .page-wrapper {
    height: auto;
    padding: 20px;
  }
  
  .code-input {
    width: 40px;
    height: 50px;
    font-size: 20px;
  }
  
  .code-inputs {
    gap: 8px;
  }
}
</style>